/**
 * 법령 요율 selector hooks
 *
 * 컴포넌트는 이 hook 들로 store 를 구독한다 — 직접 `useRatesStore` 사용 지양.
 * Firestore 갱신 시 자동 리렌더링.
 *
 * 순수 함수(`payrollCalculations.ts` 등)에서는 hook 호출 불가 — 대신
 * `useRatesStore.getState().current` 를 한 번 읽거나 인자로 rates 를 받는다.
 */

import { useRatesStore } from '../store/ratesStore';
import type {
  SizeTierApplicability,
  RegulatoryRates,
} from '../types/regulatoryRates';

// ───────────────────────── root ─────────────────────────

export const useCurrentRates = (): RegulatoryRates =>
  useRatesStore((s) => s.current);

export const useRatesIsLoading = (): boolean =>
  useRatesStore((s) => s.isLoading);

// ───────────────────────── 1. VAT ─────────────────────────

export const useVatStandardRate = (): number =>
  useRatesStore((s) => s.current.vat?.standardRate ?? 0.10);

export const useVatCardDeductRate = (): number =>
  useRatesStore((s) => s.current.vat?.creditCard?.deductRate ?? 0.013);

export const useVatCardDeductLimit = (): number =>
  useRatesStore((s) => s.current.vat?.creditCard?.deductLimit ?? 10_000_000);

// ───────────────────────── 2. 원천징수 (derived 포함) ─────────────────────────

export const useWithholdingBusinessIncome = (): number =>
  useRatesStore((s) => s.current.withholding?.businessIncome ?? 0.03);

export const useWithholdingLocalTaxRatio = (): number =>
  useRatesStore((s) => s.current.withholding?.localTaxOfWithholding ?? 0.10);

/** 사업소득 + 지방세 합산 (3.3% = 3% × 1.10) */
export const useWithholdingBusinessIncomeWithLocal = (): number =>
  useRatesStore((s) => {
    const base = s.current.withholding?.businessIncome ?? 0.03;
    const local = s.current.withholding?.localTaxOfWithholding ?? 0.10;
    return base * (1 + local);
  });

export const useSimplifiedExemptionThreshold = (): number =>
  useRatesStore(
    (s) => s.current.withholding?.simplifiedExemptionThreshold ?? 1_060_000,
  );

export const useWithholdingDailyWorkerRate = (): number =>
  useRatesStore((s) => s.current.withholding?.dailyWorker?.rate ?? 0.06);

export const useWithholdingDailyWorkerCreditRate = (): number =>
  useRatesStore((s) => s.current.withholding?.dailyWorker?.creditRate ?? 0.55);

export const useWithholdingDailyWorkerDailyExempt = (): number =>
  useRatesStore((s) => s.current.withholding?.dailyWorker?.dailyExempt ?? 150_000);

/** 지방소득세 (원천징수액의 비율) — `useWithholdingLocalTaxRatio` 별칭 */
export const useWithholdingLocalTaxRate = (): number =>
  useRatesStore((s) => s.current.withholding?.localTaxOfWithholding ?? 0.10);

export const useEiEmpSafetyUnder150 = (): number =>
  useRatesStore(
    (s) => s.current.socialInsurance?.ei?.empSafety?.under150 ?? 0.0025,
  );

// ───────────────────────── 3. 4대보험 ─────────────────────────

export const useNpsEmployerShare = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.nps?.employerShare ?? 0.0475);

export const useNpsWorkerShare = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.nps?.workerShare ?? 0.0475);

export const useNhiEmployerShare = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.nhi?.employerShare ?? 0.03595);

export const useNhiWorkerShare = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.nhi?.workerShare ?? 0.03595);

export const useLtcRateOfNhi = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.nhi?.ltcRateOfNhi ?? 0.1281);

export const useEiUnemploymentEmployer = (): number =>
  useRatesStore(
    (s) => s.current.socialInsurance?.ei?.unemployment?.employer ?? 0.009,
  );

export const useEiUnemploymentWorker = (): number =>
  useRatesStore(
    (s) => s.current.socialInsurance?.ei?.unemployment?.worker ?? 0.009,
  );

export const useWciAverageRate = (): number =>
  useRatesStore((s) => s.current.socialInsurance?.wci?.averageRate ?? 0.0147);

/** 근로자 부담 합계 = NPS + NHI + (NHI × 장기요양) + EI */
export const useInsuranceWorkerTotalShare = (): number =>
  useRatesStore((s) => {
    const si = s.current.socialInsurance;
    const nps = si?.nps?.workerShare ?? 0.0475;
    const nhi = si?.nhi?.workerShare ?? 0.03595;
    const ltc = nhi * (si?.nhi?.ltcRateOfNhi ?? 0.1281);
    const ei = si?.ei?.unemployment?.worker ?? 0.009;
    return nps + nhi + ltc + ei;
  });

// ───────────────────────── 4. 노동 ─────────────────────────

export const useMinWageHourly = (): number =>
  useRatesStore((s) => s.current.labor?.minWage?.hourly ?? 10_320);

export const useMinWageMonthly209h = (): number =>
  useRatesStore((s) => s.current.labor?.minWage?.monthly209h ?? 2_156_880);

export const useMonthlyToHourlyDivisor = (): number =>
  useRatesStore(
    (s) => s.current.labor?.workhour?.monthlyToHourlyDivisor ?? 209,
  );

export const useWeeklyMaxHours = (): number =>
  useRatesStore((s) => s.current.labor?.workhour?.weeklyMax ?? 52);

export const useDailyLegalHours = (): number =>
  useRatesStore((s) => s.current.labor?.workhour?.dailyLegal ?? 8);

export const useWeeklyRestMinHours = (): number =>
  useRatesStore((s) => s.current.labor?.weeklyRestMinHours ?? 15);

export const useOvertimeWeekdayRate = (): number =>
  useRatesStore((s) => s.current.labor?.overtime?.weekday ?? 0.5);

export const useNightPremiumRate = (): number =>
  useRatesStore((s) => s.current.labor?.overtime?.night ?? 0.5);

export const useHolidayPremiumRate = (): number =>
  useRatesStore((s) => s.current.labor?.overtime?.holidayUnder8 ?? 0.5);

export const useLaborDayPremiumRate = (): number =>
  useRatesStore((s) => s.current.labor?.overtime?.laborDay ?? 0.5);

// ───────────────────────── 5. 5인 분기 ─────────────────────────

export const useCompanySizeThreshold = (): number =>
  useRatesStore((s) => s.current.labor?.companySize?.threshold ?? 5);

const FALLBACK_TIER_OVER: SizeTierApplicability = {
  overtimeApplied: true,
  nightApplied: true,
  holidayApplied: true,
  annualLeaveApplied: true,
  weeklyHolidayApplied: true,
  severanceApplied: true,
  unfairDismissalProtected: true,
};

const FALLBACK_TIER_UNDER: SizeTierApplicability = {
  overtimeApplied: false,
  nightApplied: false,
  holidayApplied: false,
  annualLeaveApplied: false,
  weeklyHolidayApplied: true,
  severanceApplied: true,
  unfairDismissalProtected: false,
};

/** 직원 수에 따라 적용 tier 반환 (5인 미만/이상) */
export const useTierFor = (employeeCount: number): SizeTierApplicability =>
  useRatesStore((s) => {
    const size = s.current.labor?.companySize;
    if (!size) {
      return employeeCount >= 5 ? FALLBACK_TIER_OVER : FALLBACK_TIER_UNDER;
    }
    return employeeCount >= size.threshold ? size.over : size.under;
  });

export const useIsFivePlus = (employeeCount: number): boolean => {
  const threshold = useCompanySizeThreshold();
  return employeeCount >= threshold;
};

/** tier 적용 자동 분기된 연장 가산률 (5인 미만이면 0) */
export const useOvertimeWeekdayFor = (employeeCount: number): number => {
  const tier = useTierFor(employeeCount);
  const rate = useOvertimeWeekdayRate();
  return tier.overtimeApplied ? rate : 0;
};

export const useNightPremiumFor = (employeeCount: number): number => {
  const tier = useTierFor(employeeCount);
  const rate = useNightPremiumRate();
  return tier.nightApplied ? rate : 0;
};

export const useHolidayPremiumFor = (employeeCount: number): number => {
  const tier = useTierFor(employeeCount);
  const rate = useHolidayPremiumRate();
  return tier.holidayApplied ? rate : 0;
};

// ───────────────────────── 6. 정책/공휴일 ─────────────────────────

/** 해당 연도의 공휴일 [월, 일] 배열 (고정 + 변동 합산) */
export const usePublicHolidaysFor = (year: number): Array<[number, number]> =>
  useRatesStore((s) => {
    const ph = s.current.policy?.publicHolidays;
    const fixed = ph?.fixed ?? [];
    const variable = ph?.variable?.[String(year)] ?? [];
    return [...fixed, ...variable];
  });

// ───────────────────────── 7. 비과세 ─────────────────────────

export const useMealAllowanceMonthlyMax = (): number =>
  useRatesStore((s) => s.current.nonTaxable?.mealAllowanceMonthlyMax ?? 200_000);
